export const meta = {
  id: '23-instrumental-and-comitative',
  order: 23,
  checkFocus: { cases: ['ins', 'com'] },   // what the end-of-lesson check tests first
  titleFi: 'Instrumentaali ja komitatiivi',
  titleEn: 'Instrumental and comitative',
  duolingoUnit: ['duo-26-instrumental', 'duo-27-comitative'],
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: [
    'kisikagon',
    'mijegon',
    'brōzagon',
    'enkagon',
    'jomīsagon',
    'soljanna',
    'gelte',
    'zāeres',
    'krēgo',
    'belmos',
    'ātsio',
    'rōbir',
    'valyre',
    'kyno',
    'turgon',
    'raqiros',
    'kēlio',
    'kostio',
    'tolmīhy',
    'dārys',
    'ours:sōvevos',
    'ours:anevenon',
    'ours:rȳvȳdravos', 'ours:barvos', 'ours:tēmvītsos', 'ours:pilovos',
    'ours:letvos',
    'ours:jentety',
    'ours:zoklākovos',
    'ours:ubardutir',
    'ours:taksi',
    'ours:tunnelbana',
    'ours:soljy',
  ],
  exampleIds: [
    'ex-007ad739b7df',
    'ex-7f775dec21ad',
    'ex-ff7c8fb5ba82',
    'hotd-s1e02-009',
    'got-s3e05-006',
    'ex-a1f97ca11861',
    'ex-d38eaf37fca9',
    'ex-4dfb596670aa',
    'ex-1010bfbdfcba',
    'hotd-s1e02-016',
    'ex-7216f1eff677',
    'got-s7e02-007',
    'ex-2e0161ec826a',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Instrumental',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Instrumental',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Comitative',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Comitative',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar#Personal_Pronouns',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['cases'],
  grammarAlso: ['adj-position', 'personal', 'valence'],
  drills: { cases: ['ins', 'com'] },
  showLines: ['hotd-s1e02-009', 'got-s3e05-006', 'hotd-s1e02-016', 'got-s7e02-007'],
  pronunciation: { focus: 'y', words: ['valyre', 'kyno', 'tolmīhy', 'dārys', 'trēsy'], lines: ['got-s3e05-006', 'got-s7e02-007'] },
  letter: {
    fi: 'Kerro, millä ja kenen kanssa teit jotakin (instrumentaali ja komitatiivi).',
    en: 'Write what you did something with and with whom (instrumental and comitative).',
  },
  scene: 'got-s7e02-1',
}

# Instrumentaali ja komitatiivi

Kaksi sijaa, jotka ovat toistensa peilikuvat: instrumentaali kertoo, millä jotakin tehdään, ja komitatiivi, kenen kanssa. Suomessa sama ero näkyy sanoissa *veitsellä* ja *ystävän kanssa*.

## Instrumentaali

Instrumentaali on sija sille, *jolla* tai *jonka avulla* jotain tehdään: veitsi, jolla halkaistaan, miekka, jolla hyökätään, sana, jolla puhutaan. Valyrian kannalta olennaista on, että sijan perusesimerkki on **eloton väline**. Poika söi leivoksen haarukalla; tyttö hyökkäsi vihollisen kimppuun miekalla. Jos mukana on ihminen, jonka *kanssa* jotain tehdään, käytetään toista sijaa, komitatiivia, johon tutustumme seuraavaksi.

## Tuttu ajatus, eri sija

Alkuun suomesta on tässä paljon apua. Suomessa väline ilmaistaan adessiivilla: *veitsellä*, *miekalla*, *haarukalla*. Valyriassa samaan tehtävään on oma sijansa.

<Compare fi="veitse|llä" hv="egr|omy" label="adessiivi ~ instrumentaali" />

<Example id="ex-007ad739b7df" />

<Interlinear hv="Vala egr|omy rōb|ir ezīm|as." gloss="mies-NOM veitsi-INS viikuna-ACC halkaista-PRS.3SG" fi="Mies halkaisee viikunan veitsellä." />

Ero on siinä, että suomen adessiivilla on paljon muitakin tehtäviä (*pöydällä*, *minulla on*). Valyrian instrumentaali on rajatumpi: se ilmaisee välinettä, keinoa tai tapaa. Ja samoin kuin suomessa *ystävän kanssa* ja *veitsellä* ilmaistaan eri tavoin, valyria tekee saman eron kahdella sijalla.

## Instrumentaalia vaativia verbejä

Instrumentaali merkitsee myös joidenkin verbien objektia tai toista objektia. Nämä kannattaa opetella niin, että verbi ja sija kulkevat yhdessä, koska suomessa vastaavien verbien kanssa käytetään aivan muita sijoja.

**<Hv glossed>kisikagon</Hv> ’syöttää’.** Se, jota syötetään, on akkusatiivissa, ruoka instrumentaalissa.

<Example id="ex-7f775dec21ad" />

Tässä <Hv glossed>annī</Hv> on syötettävä hevonen (akkusatiivi) ja <Hv glossed>rizmoso</Hv> hiekka, jota sille syötetään (instrumentaali).

**<Hv glossed>mijegon</Hv> ’puuttua, olla ilman’.** Sen ainoa objekti on instrumentaalissa. Suomesta ei ole tässä apua: meillä se, mikä puuttuu, on nominatiivissa tai partitiivissa, ja paikka, josta se puuttuu, elatiivissa (*suusta puuttuu hampaita*).

<Example id="ex-ff7c8fb5ba82" />

**<Hv glossed>brōzagon</Hv> ’nimetä, kutsua’.** Nimettävä on akkusatiivissa ja **nimi** – se, miksi joku nimetään – instrumentaalissa. Suomessa nimi olisi translatiivissa.

<Compare fi="prinsessa|ksi" hv="dārilar|oso" hvFi="prinsessa" label="translatiivi ~ instrumentaali" />

<Line id="hotd-s1e02-009" />

<Line id="got-s3e05-006" />

Daenerysin kysymyksessä <Hv glossed>skoroso</Hv> ’mikä’ on instrumentaalissa: nimeä kysytään samalla sijalla, jolla se annetaan.

**<Hv glossed>enkagon</Hv> ’olla velkaa’.** Velkoja on akkusatiivissa, velaksi jäänyt asia instrumentaalissa.

<Example id="ex-a1f97ca11861" />

**<Hv glossed>jomīsagon</Hv> ’pitää yllään, kantaa’.** Haarniska tai vaate, jota pidetään yllä, on instrumentaalissa.

<Example id="ex-d38eaf37fca9" />

<Pronounce word="jomīsagon" />

## Muodostus yksikössä

Instrumentaalin muoto vaihtelee, mutta perusajatus on yksi. Ota sanan **genetiivi** ja lisää siihen konsonantti ja sen perään sanan teemavokaali:

- useimmat sanat saavat **s**:n: <Hv glossed>soljanna</Hv> ’peräsin’ → genetiivi <Hv>soljanno</Hv> → <Hv>soljannosa</Hv>
- **-y**- ja **-ys**-loppuiset sanat saavat **m**:n: <Hv glossed>qilōny</Hv> ’ruoska’ → <Hv>qilōno</Hv> → <Hv>qilōnomy</Hv>

<Paradigm lemma="soljanna" cases="nom,gen,ins" numbers="sg" />

<Pronounce word="soljannosa" />

Teemavokaalin kuulee yleensä jo perusmuodosta: <Hv>soljanna</Hv> päättyy a:han ja saa siksi lopun <Hv>-osa</Hv>. Samaan tapaan <Hv glossed>gelte</Hv> ’kypärä’ saa muodon <Hv>geltose</Hv>, <Hv glossed>zāeres</Hv> ’jalokivi’ muodon <Hv>zāerose</Hv>, <Hv glossed>krēgo</Hv> ’punajuuri’ muodon <Hv>krēgoso</Hv> ja <Hv glossed>belmos</Hv> ’ketjun lenkki’ muodon <Hv l="belmos">belmoso</Hv>. M-muodot päättyvät y:hyn aivan kuten perusmuotokin: <Hv glossed>rudirys</Hv> ’kuoppa’ → <Hv>rudiromy</Hv>.

<Note>Duolingo kääntää <Hv glossed>belmos</Hv> *chain* ’ketju’. Sanakirjan mukaan se on ’ketjun lenkki’ tai ’rengas’ (wikin sanakirja, Belmos). Duolingossa vastaa *chain*, muuten käytä sanakirjan merkitystä.</Note>

Yksi muutos toistuu usein: **io muuttuu ȳ:ksi**. Siksi <Hv glossed>ātsio</Hv> ’hammas’ saa instrumentaalin <Hv>ātsȳso</Hv>: genetiivin <Hv>ātsiō</Hv> io sulautuu pitkäksi ȳ:ksi. Samoin <Hv glossed>brōzi</Hv> ’nimi’ → <Hv>brōzȳsi</Hv> ja <Hv glossed>rōbir</Hv> ’viikuna’ → genetiivi <Hv>rōbrio</Hv> → <Hv>rōbrȳsi</Hv>.

<Paradigm lemma="ātsio" cases="nom,gen,ins" numbers="sg" />

<Pronounce word="ātsȳso" />

Huomaa paino: ā·TSȲ·so. Pitkä ȳ vetää painon puoleensa, vaikka suomalainen painottaisi luonnostaan ensimmäistä tavua.

## Monikko: yksi kaava kaikille

Monikko on helpompi. Monikossa jokainen instrumentaali – ja jokainen komitatiivi – muodostetaan samalla tavalla: **kahdenna konsonantti ja vaihda loppuvokaali i:ksi.**

<Compare fi="veitsi|llä" hv="egr|ommi" label="monikon adessiivi ~ monikon instrumentaali" />

Jos <Hv glossed>qilōnomy</Hv> on ’ruoskalla’, <Hv glossed>qilōnommi</Hv> on ’ruoskilla’. Jos <Hv glossed>belmoso</Hv> on ’lenkillä’, <Hv glossed>belmossi</Hv> on ’lenkeillä’. Hammasesimerkin <Hv>ātsȳssi</Hv> on juuri tällainen muoto: <Hv>ātsȳso</Hv> → <Hv>ātsȳssi</Hv>.

<Paradigm lemma="qilōny" cases="ins" numbers="sg,pl" />

## Adjektiivit instrumentaalissa

Suomessakin adjektiivi mukautuu: *terävällä veitsellä* – adjektiivi on samassa sijassa kuin substantiivi. Sama periaate pätee valyriassa, vieläpä tarkemmin: **adjektiivi seuraa substantiivin muotoa**. Kun substantiivilla on m-instrumentaali, adjektiivillakin on m-pääte; kun substantiivilla on s, adjektiivillakin on s.

Kahdessa kohdassa suomesta ei kuitenkaan ole apua. Ensinnäkin adjektiivin pääte riippuu substantiivin **suvusta** (kuu, aurinko, maa, vesi), eikä suomessa ole sukuja lainkaan. Toiseksi adjektiivin muoto riippuu siitä, onko se substantiivin **perässä** (jälkiasema) vai **edellä** (etuasema). Jälkiasemassa muodot ovat täydet:

<Paradigm lemma="kasta" filter="positive.post" cases="ins,com" numbers="sg,pl" />

Etuasemassa adjektiivista putoaa loppuvokaali tai koko viimeinen tavu, ja joskus myös sen sisävokaali muuttuu. Tavallisin tulos on lyhyt <Hv>-os</Hv> tai <Hv>-om</Hv>:

<Paradigm lemma="kasta" filter="positive.pre" cases="ins,com" numbers="sg,pl" />

<Note>
Taulukko perustuu wikin taivutustaulukkoon, jossa s-muoto (<Hv>kastosa</Hv>, <Hv>kastos</Hv>) on merkitty instrumentaaliksi ja m-muoto (<Hv>kastoma</Hv>, <Hv>kastom</Hv>) komitatiiviksi. Duolingon Tips & Notesin mukaan adjektiivi kuitenkin valitsee s:n tai m:n sen mukaan, kumpi substantiivilla on. Kun substantiivi on m-tyyppiä, kuten <Hv>azantomy</Hv>, myös instrumentaalissa oleva adjektiivi on m-muodossa.
</Note>

Katso vielä kahta virallista esimerkkiä, joissa adjektiivi on etuasemassa. Yllä olevassa velkalauseessa substantiivi on s-tyyppiä, joten omistusadjektiivikin on s-muodossa: <Hv glossed>zȳhos mīsvoso</Hv> ’hänen haarniskansa’ instrumentaalissa.

Seuraavassa substantiivi on m-tyyppiä (<Hv>azantys</Hv> → <Hv>azantomy</Hv>), joten instrumentaalissa oleva adjektiivikin on m-muodossa <Hv fi="minun">ñuhom</Hv>:

<Example id="ex-4dfb596670aa" />

<Interlinear hv="Avy ñuh|om azant|omy brōz|an." gloss="sinä-ACC minun-INS ritari-INS nimetä-PRS.1SG" fi="Nimitän sinut ritarikseni." />

Tässä lauseessa kaikki on kauniisti koolla: verbi <Hv>brōzagon</Hv>, nimettävä akkusatiivissa, nimi instrumentaalissa ja adjektiivi, joka seuraa substantiivin m:ää.

## Harjoituslauseita

<Practice hv="Azantys geltose jomīsas." fi="Ritarilla on kypärä päässään." />

<Practice hv="Riña annī krēgoso kisikas." fi="Tyttö syöttää hevoselle punajuurta." />

<Practice hv="Vala yne zāerose enkas." fi="Mies on minulle velkaa jalokiven." />

<Practice hv="Taoba geltose mijes." fi="Pojalta puuttuu kypärä." />

## Komitatiivi

Instrumentaalissa välineenä oli eloton esine: veitsi, kypärä, hiekka. Komitatiivi on sen elävä pari. Se on sija sille, **jonka kanssa** tekijä toimii, ja valyrian kannalta olennaista on, että sijan perusesimerkki on **elollinen**. Poika söi leivoksen siskonsa kanssa; tyttö hyökkäsi vihollisen kimppuun ritarin kanssa.

<Example id="ex-1010bfbdfcba" />

## Suomen oma komitatiivi

Suomessakin on komitatiivi, mutta se on harvinainen ja kankea: se näyttää aina monikolta ja vaatii yleensä omistusliitteen (*kuningas vaimoineen*, *tyttö ystävineen*). Arkikielessä sama sanotaan genetiivillä ja *kanssa*-sanalla. Valyrian komitatiivi on tavallinen, jokapäiväinen sija, ja se taipuu luvussa niin kuin muutkin.

<Compare fi="ystäv|ineen" hv="raqir|ossi" label="komitatiivi ~ komitatiivi (monikko)" />

Komitatiivin ajatus on siis suomalaiselle valmiiksi tuttu. Valyriassa sijaa vain todella käytetään.

## Muodostus: instrumentaalin peilikuva

Komitatiivi rakennetaan samoista aineksista kuin instrumentaali – genetiivistä, konsonantista ja teemavokaalista – mutta konsonantit ovat toisin päin:

- useimmat sanat saavat **m**:n: <Hv glossed>vala</Hv> ’mies’ → genetiivi <Hv>valo</Hv> → <Hv glossed>valoma</Hv> ’miehen kanssa’
- kolmannen <Term id="deklinaatio">taivutusluokan</Term> sanat ja o-teemaiset sanat saavat **s**:n: <Hv glossed>raqiros</Hv> ’ystävä’ → <Hv>raqiro</Hv> → <Hv>raqiroso</Hv>

<Paradigm lemma="vala" cases="gen,ins,com" numbers="sg" />

<Paradigm lemma="raqiros" cases="gen,ins,com" numbers="sg" />

Vertaa taulukoita. Sanalla <Hv>vala</Hv> instrumentaali ja komitatiivi eroavat (<Hv>valosa</Hv>, <Hv>valoma</Hv>). Sanalla <Hv>raqiros</Hv> ne ovat samat, sillä o-teemaisilla sanoilla komitatiivikin saa s:n. Samoin -y- ja -ys-loppuisilla sanoilla kummassakin sijassa on m. Joissakin <Term id="deklinaatio">taivutusluokissa</Term> nämä kaksi sijaa siis sulautuvat yhteen, ja silloin asiayhteys kertoo, kumpi on kyseessä: elävä seuralainen vai eloton väline.

Lisää esimerkkejä, kaikki saman periaatteen mukaan:

- m-ryhmä: <Hv glossed>valonqar</Hv> ’pikkuveli’ → <Hv>valonqroma</Hv>; <Hv glossed>zaldrīzes</Hv> ’lohikäärme’ → <Hv>zaldrīzome</Hv>; <Hv glossed>dārys</Hv> ’kuningas’ → <Hv>dāromy</Hv>; <Hv glossed>tolmīhy</Hv> ’muukalainen’ → <Hv l="tolmīhy">tolmīhomy</Hv>
- s-ryhmä: <Hv glossed>kyno</Hv> ’silkkiäistoukka’ → <Hv>kynoso</Hv>; <Hv glossed>turgon</Hv> ’mato’ → <Hv>turgoso</Hv>
- io muuttuu ȳ:ksi tässäkin: <Hv glossed>kostio</Hv> ’sankari’ → <Hv l="kostio">kostȳso</Hv>; <Hv glossed>kēli</Hv> ’kissa’ → genetiivi <Hv l="kēli">kēlio</Hv> → <Hv>kēlȳmi</Hv>; <Hv glossed>qintir</Hv> ’kilpikonna’ → <Hv>qintrȳmi</Hv>

<Note>
Tämän oppitunnin sanoissa on <Hv glossed>kēlio</Hv> ’leijona’. Täsmälleen samannäköinen on myös sanan <Hv glossed>kēli</Hv> ’kissa’ genetiivi ’kissan’. Ne ovat eri sanoja, jotka vain sattuvat näyttämään samalta; asiayhteys erottaa ne.
</Note>

<Pronounce word="zaldrīzome" />

<Pronounce word="kēlȳmi" />

Muista paino. Suomalainen painottaisi vaistomaisesti ensimmäistä tavua, mutta <Hv>kēlȳmi</Hv> on kē·LȲ·mi: pitkä vokaali toiseksi viimeisessä tavussa vetää painon puoleensa. Sanassa <Hv>zaldrīzome</Hv> toiseksi viimeinen tavu <Hv part>zo</Hv> on kevyt ja sitä edeltävä <Hv part>drī</Hv> raskas, joten paino on kolmanneksi viimeisellä tavulla: zal·DRĪ·zo·me.

<Line id="hotd-s1e02-016" />

Daemonin repliikissä <Hv glossed>zaldrīzome</Hv> on ’lohikäärmeen kanssa’: puhuteltava jakoi kehtonsa lohikäärmeen kanssa.

## Monikko: sama kaava kuin instrumentaalissa

Monikossa ei ole mitään uutta: **kahdenna konsonantti ja vaihda loppuvokaali i:ksi.** Jos <Hv glossed>valoma</Hv> on ’miehen kanssa’, <Hv glossed>valommi</Hv> on ’miesten kanssa’. Jos <Hv glossed>raqiroso</Hv> on ’ystävän kanssa’, <Hv glossed>raqirossi</Hv> on ’ystävien kanssa’.

<Paradigm lemma="vala" cases="com" numbers="sg,pl" />

## Adjektiivit seuraavat substantiivia

Adjektiivit mukautuvat täsmälleen samoin kuin instrumentaalissa: kun substantiivilla on m-komitatiivi, adjektiivillakin on m-pääte, ja etuasemassa adjektiivi lyhenee. Suku ratkaisee jälleen päätteen vokaalin, eikä suomesta ole siinä apua. Tässä virallisessa lauseessa substantiivi <Hv glossed>kepa</Hv> ’isä’ on m-tyyppiä, ja kaksi etuasemassa olevaa adjektiivia seuraa sitä:

<Example id="ex-7216f1eff677" />

<Interlinear hv="Vala zȳh|om lyk|om kep|oma ozmije|s." gloss="mies-NOM hänen-COM rauhallinen-COM isä-COM kaivata-PRS.3SG" fi="Mies kaipaa rauhallista isäänsä." />

Huomaa myös verbi. Verbin <Hv glossed>ozmijegon</Hv> ’kaivata’ kanssa kaivattu ihminen on komitatiivissa ja kaivattu asia instrumentaalissa. Elollinen ja eloton jakavat taas sijat keskenään.

## Pronominit

Persoonapronomineilla on omat komitatiivi- ja instrumentaalimuotonsa.

**Ensimmäisellä ja toisella persoonalla** on vain m-muoto, joka toimii kummassakin sijassa:

- <Hv glossed>ynoma</Hv> ’minun kanssani’
- <Hv glossed>aōma</Hv> ’sinun kanssasi’
- <Hv glossed>īloma</Hv> ’meidän kanssamme’
- <Hv glossed>jemme</Hv> ’teidän kanssanne’

**Kolmannella persoonalla** on kummallekin sijalle oma muotonsa: s-muoto on instrumentaali, m-muoto komitatiivi. Yksikössä muotoja on kaksi paria sen mukaan, onko tarkoite kuu- tai aurinkosukua vai maa- tai vesisukua:

- <Hv>zijosy</Hv> / <Hv>zijomy</Hv> – kuu- ja aurinkosuku
- <Hv>josa</Hv> / <Hv>joma</Hv> – maa- ja vesisuku
- <Hv>pōntosa</Hv> / <Hv>pōntoma</Hv> – monikko

<Pronounce word="ynoma" />

<Line id="got-s7e02-007" />

Melisandren lauseessa <Hv glossed>zijomy</Hv> on ’sen mukana’: kuolleet tulevat pitkän yön mukana. Lopuksi Daenerysin kysymys armeijalleen:

<Example id="ex-2e0161ec826a" />

## Harjoituslauseita

<Practice hv="Ābra dāromy ipradas." fi="Nainen syö kuninkaan kanssa." />

<Practice hv="Raqiros ynoma iprattas." fi="Ystävä söi kanssani." />

<Practice hv="Taoba raqirossi iprattas." fi="Poika söi ystäviensä kanssa." />

<Practice hv="Vala geviom ābroma ipradas." fi="Mies syö kauniin naisen kanssa." />

## Kertaus: kahdeksan sijaa, yksi mies

Nyt kaikki kahdeksan sijaa on käyty läpi. Damien Halmen (Academy of High Valyrian) kertaustaulukko, korjattuna sanakirjan taivutustaulukoiden mukaan. Lauseet ovat harjoituslauseita, eivät Petersonin.

<Practice hv="Vala dekurūbas." fi="Mies kävelee." />

<Practice hv="Ziry vale arghus." fi="Hän metsästää miestä." />

<Practice hv="Valo korze issa." fi="Se on miehen miekka." />

<Practice hv="Īlon valot perzi tepi." fi="Annamme miehelle tulta." />

<Practice hv="Va valā memēbās!" fi="Marssi miehen luo!" />

<Practice hv="Morghon tymptir valosa tymas." fi="Kuolema pelaa peliään miehellä." />

<Practice hv="Ziry valoma vāedas." fi="Hän laulaa miehen kanssa." />

<Practice hv="Rytsas, valus!" fi="Hei, mies!" />

Sama minä-pronominilla:

<Practice hv="Nyke dekurūban." fi="Minä kävelen." />

<Practice hv="Ziry yne arghus." fi="Hän metsästää minua." />

<Practice hv="Pōnta ynot perzi tepsi." fi="He antavat minulle tulta." />

<Practice hv="Va nykē memēbās!" fi="Marssi minun luokseni!" />

<Practice hv="Ziry ynoma vāedas." fi="Hän laulaa kanssani." />

Pronominilla instrumentaali ja komitatiivi ovat sama muoto <Hv>ynoma</Hv>.

<Note>
**Sijojen tunnuskirjaimet.** Damien Halmen (Academy of High Valyrian) muistisääntö: datiivissa on *t* (<Hv>valot</Hv>), genetiivissä *o* (<Hv>valo</Hv>), instrumentaalissa *s* tai *z* (<Hv>valosa</Hv>) ja komitatiivissa *m* (<Hv>valoma</Hv>). Monikossa datiivi, genetiivi ja lokatiivi ovat useimmiten sama muoto (<Hv>valoti</Hv>). Poikkeus on toinen <Term id="deklinaatio">taivutusluokka</Term>, jonka monikon lokatiivi päättyy pitkään *ī*:hin (<Hv>azantī</Hv>). Muista myös, että joissakin <Term id="deklinaatio">taivutusluokissa</Term> instrumentaali ja komitatiivi ovat samat (<Hv>raqiroso</Hv>).
</Note>

## Kuka teki kenelle?

Valitse, kumpi suomennos on oikea. Sen kertovat päätteet, ei sanajärjestys.

<KukaTeki />

## Sanat

<Vocab />

<Check />
